// Runtime-derived cross-origin URL config — see the tenant-onboarding sibling
// (Frontend/tenant-onboarding/src/lib/runtimeConfig.ts) for the full design
// rationale. Same shape lives here so ds-external's bundle is also fully
// env-agnostic: one artefact, three envs, URLs derived from
// window.location.hostname at runtime.
//
// ds-external is the unauthenticated review/sign portal — token-based access
// (the URL path carries the reviewer's access token), no OIDC, no session
// cookie. Cross-origin calls to api.<apex> are CORS-allowlisted by
// BFF_ALLOWED_ORIGIN_DOMAIN={{ public_domain }} on the api-gateway side.

function computeApex(): string | null {
  if (typeof window === 'undefined' || !window.location) return null;
  const host = window.location.hostname;
  // Local-dev shapes — every form collapses to "no apex, use fallbacks":
  //   localhost                 — `pnpm dev` default
  //   127.0.0.1 / numeric IPv4  — direct-IP access
  //   ::1 / [::1]               — IPv6 loopback (browsers normalise to ::1)
  //   *.localhost               — /etc/hosts subdomain debug
  if (host === 'localhost' || host === '::1' || host.endsWith('.localhost')) return null;
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(host)) return null;
  if (!host.includes('.')) return null;
  // external.myworkpay.site → myworkpay.site
  return host.split('.').slice(1).join('.');
}

const APEX = computeApex();

/**
 * API origin for cross-origin BFF + service calls. Empty in local dev so
 * axios baseURL stays same-origin (Vite dev proxy / local docker stack).
 * Otherwise resolves to `https://api.<apex>` matching the env.
 */
export const API_BASE: string = APEX ? `https://api.${APEX}` : '';

/**
 * OnlyOffice Document Server public URL for the reviewer's editor embed.
 * Falls back to localhost:8088 docker-compose default in local dev.
 */
export const ONLYOFFICE_URL: string = APEX ? `https://api.${APEX}/office` : 'http://localhost:8088';

/**
 * Public static-assets CDN base (hero, flags, logo, brand fonts). Per-env apex
 * is derived at runtime; local dev falls back to the dev CDN (assets are public).
 */
export const STATIC_BASE: string = APEX ? `https://static.${APEX}` : 'https://static.myworkpay.site';

/**
 * Customer-facing workspace domain suffix shown in the login UI (e.g.
 * ".myworkpay.site"). Derived from the runtime apex so it matches the host we
 * actually redirect to (`<sub>.<apex>/login`) — never hardcode ".com". Local dev
 * falls back to the dev apex.
 */
export const WORKSPACE_DOMAIN_SUFFIX: string = APEX ? `.${APEX}` : '.myworkpay.site';

/**
 * v3 (legacy) portal login URL for the "Go back to Main Login" link on
 * /eor/workspace. The v3 portal lives on its own domains (not derived from the
 * apex), so this is a per-env lookup keyed on the runtime apex. Local dev and
 * unknown apexes fall back to the dev portal.
 */
const V3_PORTAL_URLS: Record<string, string> = {
  'myworkpay.app': 'https://app.myworkpay.com/',
  'myworkpay.xyz': 'https://staging.myworkpay.com/',
  'myworkpay.site': 'https://new.workpay.co.ke/',
};

export const V3_PORTAL_URL: string =
  (APEX ? V3_PORTAL_URLS[APEX] : undefined) ?? 'https://new.workpay.co.ke/';
